import React, {ReactNode, useState, forwardRef, useImperativeHandle} from 'react';
import AppDialog, {AppDialogProps} from './AppDialog';
import {Avatar, Breakpoint, Stack} from '@mui/material';
import {Typography} from '@mui/material';

export interface AppConfirmationDialogRefType {
    handleOpen: () => void;
    handleClose: () => void;
    open: boolean;
}
interface AppConfirmationDialogProps extends Omit<AppDialogProps, 'open' | 'onCloseDialog' | 'children'> {
    onConfirmClick?: (open: boolean, handleOpen: () => void, handleClose: () => void) => void;
    title: string;
    icon?: ReactNode;
    description: string | React.ReactNode;
    submitButtonColor?: 'error' | 'primary' | 'secondary' | undefined;
    alignContainer?: 'center' | 'right' | 'left' | 'inherit' | 'justify' | undefined;
    renderElement: (open: boolean, handleOpen: () => void) => ReactNode;
    maxWidth?: false | Breakpoint | undefined;
    loading?: boolean;
}
export const AppConfirmationDialog = forwardRef<AppConfirmationDialogRefType, AppConfirmationDialogProps>(
    (
        {
            onConfirmClick,
            title,
            submitButtonColor = 'error',
            description,
            icon,
            renderElement,
            alignContainer = 'center',
            maxWidth = 'xs',
            loading,
            ...rest
        },
        ref,
    ) => {
        const [open, setOpen] = useState(false);
        const handleOpen = () => {
            setOpen(true);
        };

        const handleClose = () => {
            setOpen(false);
        };

        useImperativeHandle(ref, () => ({
            handleOpen,
            handleClose,
            open,
        }));

        return (
            <>
                {renderElement(open, handleOpen)}
                <AppDialog
                    open={open}
                    onCloseDialog={handleClose}
                    dialogContainerProps={{sx: {mt: 2, my: 1}}}
                    fullWidth
                    maxWidth={maxWidth}
                    footerProps={{
                        onSubmitLabel: 'Confirm',
                        onCancelClick: () => {
                            handleClose();
                        },
                        onSubmitClick: () => {
                            if (onConfirmClick) {
                                onConfirmClick(open, handleOpen, handleClose);
                            }
                        },
                        submitButtonProps: {
                            fullWidth: true,
                            color: submitButtonColor,
                            isLoading: loading,
                        },
                        cancelButtonProps: {
                            fullWidth: true,
                            color: 'secondary',
                            variant: 'outlined',
                        },
                    }}
                    {...rest}
                >
                    <Stack alignItems={alignContainer} spacing={3.5}>
                        {icon && (
                            <Avatar color="error" sx={{width: 72, height: 72, fontSize: '1.75rem'}}>
                                {icon}
                            </Avatar>
                        )}
                        <Stack spacing={2}>
                            <Typography variant="h4" align={alignContainer}>
                                {title}
                            </Typography>
                            <Typography align={alignContainer}>{description}</Typography>
                        </Stack>
                    </Stack>
                </AppDialog>
            </>
        );
    },
);

export default AppConfirmationDialog;
